import React, { useEffect, useRef, useState } from "react";
import { createUUID } from "../../utils/index";
import { useHistory, Prompt } from "react-router-dom";
import { timeAgo } from "../../utils/index";
import styles from "./index.module.scss";
import axios from "axios";
import FileUpload from "../../components/FileUpload";
import ImageDisplay, { isImageMessage } from "../../components/ImageDisplay";
import mainSquare from "../../logo.svg";
import ThemeSwitcher from "../../components/ThemeSwitcher";

// One of the following themes
import "@simonwep/pickr/dist/themes/classic.min.css"; // 'classic' theme
import "@simonwep/pickr/dist/themes/monolith.min.css"; // 'monolith' theme
import "@simonwep/pickr/dist/themes/nano.min.css"; // 'nano' theme

var uuid = createUUID();

const PEER_DISCONNECTED_FUNC = (uuid) => {
  return JSON.stringify({
    event: "PEER_DISCONNECTED",
    uuid,
  });
};

const host = window.location.origin.includes("localhost")
  ? "https://localhost:8443"
  : "https://api.puipi.com";

function App() {
  const inputRef = useRef(null);
  const roomId = window.location.pathname.replace("/chat/", "");
  const history = useHistory();
  const [serverConnection, setServerConnection] = useState("");
  const [chatStatus, setChatStatus] = useState("connecting");
  const [messages, setMessages] = useState(
    window.localStorage.getItem(`chat-history-${roomId}`)
      ? JSON.parse(window.localStorage.getItem(`chat-history-${roomId}`))
      : []
  );
  const [peerLeft, setPeerLeft] = useState(false);
  const isLeavingRef = useRef(false);
  const roomPreviewId = roomId.slice(0, 8).toUpperCase();
  const roomSearchParams = new URLSearchParams(window.location.search);
  const selfPetType = roomSearchParams.get("selfPetType") || "";
  const peerPetType = roomSearchParams.get("peerPetType") || "";
  const hasPetTypeSummary = Boolean(selfPetType || peerPetType);

  const createConnection = () =>
    new WebSocket(
      window.location.origin.includes("localhost")
        ? `wss://localhost:8443${window.location.pathname}`
        : `wss://api.puipi.com${window.location.pathname}`,
      [uuid]
    );

  const send = function (message, callback) {
    if (!serverConnection) {
      return;
    }
    waitForConnection(function () {
      serverConnection.send(message);
      if (typeof callback !== "undefined") {
        callback();
      }
    }, 1000);
  };

  const waitForConnection = function (callback, interval) {
    if (serverConnection.readyState === 1) {
      callback();
    } else {
      setTimeout(function () {
        waitForConnection(callback, interval);
      }, interval);
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isLeavingRef.current) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      window.localStorage.setItem(
        `chat-history-${roomId}`,
        JSON.stringify(messages)
      );
      const msgBoxEle = document.querySelector("#message_box");
      msgBoxEle.scrollTo(0, msgBoxEle.scrollHeight);
    }
  }, [messages]);

  useEffect(() => {
    setServerConnection(createConnection());
  }, [uuid]);

  useEffect(() => {
    if (!serverConnection) {
      return;
    }

    let heartBeat;

    serverConnection.onclose = () => {
      console.log("ws closed");
      if (isLeavingRef.current) return;
      setChatStatus("reconnecting");
      setServerConnection(createConnection());
    };

    serverConnection.onopen = () => {
      console.log("Websocket connected [room]");
      setChatStatus("connected");
      heartBeat = setInterval(() => {
        send(JSON.stringify({ h: 1 }));
      }, 10000);
    };
    serverConnection.onmessage = (e) => {
      const payload = JSON.parse(e.data);
      if (payload.event === "PEER_DISCONNECTED") {
        setPeerLeft(true);
        setMessages((prev) => [
          ...prev,
          {
            text: "對方已離開聊天室",
            time: Date.now(),
            isSystem: true,
          },
        ]);
        return;
      }
      if (payload.event === "text") {
        if (payload.userId === uuid) {
          setMessages((prev, curr) => [
            ...prev,
            {
              text: payload.data,
              time: Date.now(),
              isSelf: true,
            },
          ]);
        } else {
          setMessages((prev, curr) => [
            ...prev,
            {
              text: payload.data,
              time: Date.now(),
              isSelf: false,
            },
          ]);
        }
      }
    };

    return () => {
      if (heartBeat) {
        clearInterval(heartBeat);
      }
    };
  }, [serverConnection]);

  function handleDisconnect() {
    if (!window.confirm("確定要離開聊天室嗎？離開後將無法回到此聊天。")) return;
    isLeavingRef.current = true;
    send(PEER_DISCONNECTED_FUNC(uuid));
    if (serverConnection && serverConnection.readyState <= 1) {
      serverConnection.close();
    }
    axios.post(`${host}/end-conversation`, {
      roomId,
      chatContent: window.localStorage.getItem(`chat-history-${roomId}`),
    });
    window.localStorage.removeItem(`chat-history-${roomId}`);
    history.push("/");
  }

  function handleEnter(e) {
    if (e.keyCode === 13) {
      e.preventDefault(); // Ensure it is only this code that runs
      sendMessage(inputRef.current.value);
    }
  }

  const sendMessage = (inputMessage) => {
    if (!inputMessage) {
      return;
    }
    const cleanInput = inputMessage.trim();
    if (!cleanInput) {
      return;
    }
    if (inputMessage.length > 1000) {
      alert("訊息太長");
      return;
    }
    send(
      JSON.stringify({
        event: "text",
        userId: uuid,
        data: cleanInput,
      })
    );
    inputRef.current.value = "";
  };

  return (
    <div className={`App ${styles.chatApp}`}>
      <Prompt
        when={true}
        message={() => {
          if (isLeavingRef.current) return true;
          return "確定要離開聊天室嗎？離開後將無法回到此聊天。";
        }}
      />
      <header className={styles.chatHeader}>
        <div className={styles.headerBrand}>
          <div className={styles.brandTile}>
            <img src={mainSquare} alt="PuiPi Logo" className={styles.mainSquareImage} />
          </div>
          <div>
            <div className={styles.headerTitle}>寵物主人聊天室</div>
            <div className={styles.headerSubtitle}>房間編號 #{roomPreviewId}</div>
          </div>
        </div>
        <div className={styles.headerActions}>
          <ThemeSwitcher />
          <span
            className={`${styles.statusBadge} ${
              chatStatus === "connected" ? styles.statusOnline : styles.statusReconnecting
            }`}
          >
            {chatStatus === "connected" ? "連線中" : "重新連線中"}
          </span>
          <button
            type="button"
            className={styles.leave_btn}
            onClick={() => {
              handleDisconnect();
            }}
          >
            結束聊天
          </button>
        </div>
      </header>
      {hasPetTypeSummary && (
        <section className={styles.petTypeSummary} aria-label="寵物類型摘要">
          <div className={styles.petTypeCard}>
            <span className={styles.petTypeLabel}>你選了什麼</span>
            <span className={styles.petTypeValue}>{selfPetType || "未提供"}</span>
          </div>
          <div className={styles.petTypeCard}>
            <span className={styles.petTypeLabel}>對方選了什麼</span>
            <span className={styles.petTypeValue}>{peerPetType || "未提供"}</span>
          </div>
        </section>
      )}
      <div id="message_box" className={styles.message_box}>
        {!messages.length && (
          <div className={styles.emptyState}>
            <h2>開始和其他寵物主人交流吧</h2>
            <p>你可以分享飼養心得、日常趣事，或上傳毛孩、寵物照片一起聊天。</p>
          </div>
        )}
        {messages?.map((message, idx) =>
          message.isSystem ? (
            <div key={`${message.time}-${idx}`} className={styles.systemMessageRow}>
              <div className={styles.systemMessage}>{message.text}</div>
            </div>
          ) : (
            <div
              key={`${message.time}-${idx}`}
              className={`${styles.messageRow} ${
                message.isSelf ? styles.messageRowSelf : styles.messageRowPeer
              }`}
            >
              <div className={styles.avatar}>{message.isSelf ? "我" : "友"}</div>
              <div
                className={`${styles.text_message} ${
                  message.isSelf ? styles.selfMessage : styles.peerMessage
                }`}
              >
                <div className={styles.messageMeta}>
                  {message.isSelf ? "你" : "對方"} ・ {timeAgo(message.time)}
                </div>
                {!isImageMessage(message.text) ? message.text : null}
                <ImageDisplay imageUrl={message.text} />
              </div>
            </div>
          )
        )}
      </div>
      {peerLeft && (
        <div className={styles.peerLeftBanner}>
          對方已離開聊天室
          <button
            type="button"
            className={styles.peerLeftBtn}
            onClick={() => {
              isLeavingRef.current = true;
              axios.post(`${host}/end-conversation`, {
                roomId,
                chatContent: window.localStorage.getItem(`chat-history-${roomId}`),
              });
              window.localStorage.removeItem(`chat-history-${roomId}`);
              history.push("/");
            }}
          >
            返回首頁
          </button>
        </div>
      )}
      <div className={styles.composerPanel}>
        <div className={styles.action_footer}>
          <input
            onKeyDown={handleEnter}
            className={styles.text_input}
            ref={inputRef}
            placeholder="輸入你想聊的毛孩、寵物話題..."
            aria-label="聊天輸入框"
          />
          <button
            type="button"
            className={styles.send_btn}
            onClick={() => {
              sendMessage(inputRef.current.value);
            }}
          >
            送出訊息
          </button>
        </div>
        <div className={styles.uploadContainer}>
          <FileUpload sendMessage={sendMessage} />
        </div>
      </div>
    </div>
  );
}

export default App;
